import React, { useState, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { axiosInstance } from "../../../utils/axios";
import useAuth from "../../../hooks/useAuth";
import { useSpace } from "../../../contexts/SpaceContext";
import { DataGridTable } from "../../../components/Common/Table";
import ReusableTable from "../../../components/Common/ReusableTable";
import {
  Alert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Checkbox,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Paper as MuiPaper,
  TablePagination,
  Tooltip,
  Typography,
  Snackbar,
  Menu,
  MenuItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card as MuiCard,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
} from "@mui/icons-material";
import { spacing } from "@mui/system";
import SearchBar from "../../components/SearchBar";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Card = styled(MuiCard)(spacing);

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// Event Type Card Component
const EventTypeCard = ({
  data: eventType,
  selected: isSelected,
  onSelect,
  index,
}) => {
  const serialNumber = index + 1;
  const stripHtmlAndLimitText = (html, limit = 100) => {
    // Create a temporary div to parse HTML content
    const temp = document.createElement("div");
    temp.innerHTML = html;

    // Get text content without HTML tags
    const plainText = temp.textContent || temp.innerText || "";

    // Limit to specified character count
    if (plainText.length <= limit) {
      return plainText;
    }

    // Return truncated text with ellipsis
    return plainText.substring(0, limit) + "...";
  };

  return (
    <Paper>
      <Card>
        <div style={{ position: "relative" }}>
          <div
            style={{
              backgroundColor: "#f0f0f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "140px",
            }}
          >
            <Typography variant="h2" color="textSecondary">
              {eventType.title.charAt(0).toUpperCase()}
            </Typography>
          </div>
          <Checkbox
            checked={isSelected}
            onChange={(e) => onSelect(e, eventType.id)}
            style={{ position: "absolute", top: "8px", left: "8px" }}
          />
        </div>
        <CardContent>
          <Box>
            <Typography variant="h5" gutterBottom>
              {eventType.title}
            </Typography>
          </Box>
          <Typography mb={2} color="textSecondary" component="p">
            S.No: {serialNumber}
            <br />
            URL: {eventType.url}
            <br />
            Duration: {eventType.duration} Min's
            <br />
            Timezone: {eventType.timezone || "UTC"}
            <br />
            {eventType.teamId && (
              <span>
                Team Event
                <br />
              </span>
            )}
            {stripHtmlAndLimitText(eventType.description, 60)}
          </Typography>
        </CardContent>
      </Card>
    </Paper>
  );
};

// Updated GraphQL query to support team filtering
const GET_EVENT_TYPES = `
  query GetEventTypes($spaceId: ID, $teamId: ID, $filterType: String) {
    getEventTypes(spaceId: $spaceId, teamId: $teamId, filterType: $filterType) {
      id
      title
      url
      duration
      description
      timezone
      availabilityId
      teamId
    }
  }
`;

const GET_TEAMS = `
  query GetTeams {
    getTeams {
      id
      name
      url
    }
  }
`;

const GET_AVAILABILITIES = `
  query GetAllAvailabilities {
    getAllAvailabilities {
      id
      name
    }
  }
`;

function EventTypes() {
  const { user } = useAuth();
  const location = useLocation();
  const { currentSpace } = useSpace();
  const message = location.state?.message;

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [eventTypes, setEventTypes] = useState([]);
  const [teams, setTeams] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [anchorElNewEvent, setAnchorElNewEvent] = useState(null);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const openNewEventMenu = Boolean(anchorElNewEvent);
  const openFilterMenu = Boolean(anchorElFilter);

  // States for filtering
  const [filterType, setFilterType] = useState("all"); // "all", "personal", "team", "allTeams"
  const [selectedTeamId, setSelectedTeamId] = useState("");

  // States for data grid
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("title");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewStyle, setViewStyle] = useState("List");
  const [searchQuery, setSearchQuery] = useState("");
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);

  // Get spaceId from query params or use current space - using useMemo to avoid recreation on every render
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const currentSpaceId = queryParams.get("spaceId") || currentSpace?.id;

  // Check for filter params in URL
  useEffect(() => {
    const urlFilterType = queryParams.get("filterType");
    const urlTeamId = queryParams.get("teamId");

    if (urlFilterType) {
      setFilterType(urlFilterType);
    }

    if (urlTeamId) {
      setSelectedTeamId(urlTeamId);
    }
  }, [queryParams]);

  // Define URL functions inside the component
  const getEventTypeAddUrl = () => {
    return `/calendar/add-event-types?spaceId=${currentSpaceId}`;
  };

  // For teams menu items
  const getTeamEventTypeUrl = (teamId) => {
    return `/calendar/add-event-types?teamId=${teamId}&spaceId=${currentSpaceId}`;
  };

  // Reset page when search query changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery, filterType, selectedTeamId]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Filter event types based on search query
  const filteredEventTypes = eventTypes.filter((eventType) => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();
    return (
      eventType.title.toLowerCase().includes(query) ||
      eventType.url.toLowerCase().includes(query) ||
      eventType.id.toString().toLowerCase().includes(query) ||
      (eventType.description &&
        eventType.description.toLowerCase().includes(query))
    );
  });

  const handleClickNewEvent = (event) => {
    setAnchorElNewEvent(event.currentTarget);
  };

  const handleCloseNewEvent = () => {
    setAnchorElNewEvent(null);
  };

  // Check if user has custom availabilities (excluding "Default Availability") before allowing to create event type
  const handleCreateEventType = (url) => {
    handleCloseNewEvent();
    console.log(
      "Checking availabilities before creating event type:",
      availabilities
    );

    // Filter out "Default Availability" - user must create their own custom availability
    const customAvailabilities = availabilities.filter(
      (a) => a.name !== "Default Availability"
    );
    console.log("Custom availabilities count:", customAvailabilities.length);

    if (!customAvailabilities || customAvailabilities.length === 0) {
      setAlert({
        open: true,
        message:
          "Please add an availability first before creating an event type. Go to Calendar > Availability to create one.",
        severity: "warning",
      });
      return;
    }
    // Navigate to the add event type page
    window.location.href = url;
  };

  const handleClickFilter = (event) => {
    setAnchorElFilter(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setAnchorElFilter(null);
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = eventTypes.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleBulkDelete = () => {
    setBulkDeleteDialog(true);
  };

  const handleBulkEdit = () => {
    if (selected.length !== 1) {
      setAlert({
        open: true,
        message: "Please select exactly one event type to edit",
        severity: "info",
      });
      return;
    }

    // Navigate to edit page
    window.location.href = `/calendar/edit-event-type/${selected[0]}`;
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      const deletePromises = selected.map((id) =>
        axiosInstance.post("/graphql", {
          query: `
            mutation DeleteEventType($id: ID!) {
              deleteEventType(id: $id)
            }
          `,
          variables: { id },
        })
      );

      await Promise.all(deletePromises);
      setEventTypes((prev) =>
        prev.filter((eventType) => !selected.includes(eventType.id))
      );
      setSelected([]);
      setAlert({
        open: true,
        message: `Successfully deleted ${selected.length} event types`,
        severity: "success",
      });
    } catch (error) {
      setAlert({
        open: true,
        message: `Error deleting event types: ${error.message}`,
        severity: "error",
      });
    } finally {
      setBulkDeleteDialog(false);
    }
  };

  // Fetch event types with team filtering
  useEffect(() => {
    const fetchEventTypes = async () => {
      try {
        const variables = {
          spaceId: currentSpaceId,
          filterType: filterType,
        };

        // Add teamId if filtering by a specific team
        if (filterType === "team" && selectedTeamId) {
          variables.teamId = selectedTeamId;
        }

        console.log("Fetching event types with variables:", variables);

        const response = await axiosInstance.post("/graphql", {
          query: GET_EVENT_TYPES,
          variables: variables,
        });

        if (response.data?.errors) {
          console.error(response.data.errors);
        } else {
          // Just use the event types as returned
          setEventTypes(response.data?.getEventTypes || []);
        }
      } catch (error) {
        console.error("Error fetching event types:", error);
        setAlert({
          open: true,
          message: "Error fetching event types",
          severity: "error",
        });
      }
    };

    if (currentSpaceId) {
      fetchEventTypes();
    }
  }, [currentSpaceId, filterType, selectedTeamId]);

  // Fetch teams for the dropdown
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await axiosInstance.post("/graphql", {
          query: GET_TEAMS,
        });

        if (response.errors) {
          console.error(response.errors);
        } else {
          setTeams(response.data?.getTeams || []);
        }
      } catch (error) {
        console.error("Error fetching teams:", error);
      }
    };

    fetchTeams();
  }, []);

  // Fetch availabilities to check if user can create event types
  useEffect(() => {
    const fetchAvailabilities = async () => {
      try {
        const response = await axiosInstance.post("/graphql", {
          query: GET_AVAILABILITIES,
        });

        console.log("Availabilities response:", response.data);

        // Check multiple response structures
        let availabilityData = null;
        if (response.data?.data?.getAllAvailabilities) {
          availabilityData = response.data.data.getAllAvailabilities;
        } else if (response.data?.getAllAvailabilities) {
          availabilityData = response.data.getAllAvailabilities;
        }

        if (availabilityData && availabilityData.length > 0) {
          setAvailabilities(availabilityData);
          console.log("Found availabilities:", availabilityData.length);
        } else {
          setAvailabilities([]);
          console.log("No availabilities found");
        }
      } catch (error) {
        console.error("Error fetching availabilities:", error);
        setAvailabilities([]);
      }
    };

    fetchAvailabilities();
  }, []);

  // Helper to strip HTML and limit text for description
  const stripHtmlAndLimitText = (html, limit = 100) => {
    if (!html) return "";

    // Create a temporary div to parse HTML content
    const temp = document.createElement("div");
    temp.innerHTML = html;

    // Get text content without HTML tags
    const plainText = temp.textContent || temp.innerText || "";

    // Limit to specified character count
    if (plainText.length <= limit) {
      return plainText;
    }

    // Return truncated text with ellipsis
    return plainText.substring(0, limit) + "...";
  };

  // Generate preview URL for an event - using new slug-based format
  const getPreviewUrl = (eventType) => {
    // Use the authenticated user's name, or fallback to "user" if not available
    const username = user?.name?.toLowerCase().replace(/\s+/g, "-") || "user";
    return `/preview/events/preview/${username}/${eventType.url}`;
  };

  // Define table columns for ReusableTable
  const columns = useMemo(
    () => [
      {
        field: "serialNumber",
        headerName: "S.No",
        width: 70,
        sortable: false,
        filterable: false,
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1,
        renderCell: (params) => (
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">
              {params.row.title}
              {params.row.teamId && (
                <Chip
                  size="small"
                  label="Team"
                  color="primary"
                  variant="outlined"
                  sx={{ ml: 1, height: 20 }}
                />
              )}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {stripHtmlAndLimitText(params.row.description, 100)}
            </Typography>
          </Box>
        ),
      },
      {
        field: "url",
        headerName: "URL",
        flex: 0.7,
      },
      {
        field: "duration",
        headerName: "Duration",
        width: 100,
        valueGetter: (params) => `${params.row.duration} Min's`,
      },
      {
        field: "timezone",
        headerName: "Timezone",
        width: 150,
        valueGetter: (params) => params.row.timezone || "UTC",
      },
      {
        field: "preview",
        headerName: "Preview",
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title="Preview Event">
            <IconButton
              component={Link}
              to={getPreviewUrl(params.row)}
              target="_blank"
              color="primary"
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    []
  );

  // Prepare data with serial numbers
  const processedEventTypes = useMemo(() => {
    return eventTypes.map((eventType) => ({
      ...eventType,
      description: stripHtmlAndLimitText(eventType.description, 100),
    }));
  }, [eventTypes]);

  // Search fields
  const searchFields = ["title", "url", "description"];

  return (
    <React.Fragment>
      <Helmet title="Event Types" />

      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Event Types
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to={`/spaces/${currentSpaceId}`}>
              {currentSpace?.name || "Spaces"}
            </Link>
            <Link
              component={NavLink}
              to={`/calendar/dashboard?spaceId=${currentSpaceId}`}
            >
              Calendar
            </Link>
            <Typography color="text.primary">Event Types</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <div>
            <Button
              variant="contained"
              color="primary"
              id="new-event-button"
              aria-controls={openNewEventMenu ? "new-event-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={openNewEventMenu ? "true" : undefined}
              onClick={handleClickNewEvent}
              startIcon={<AddIcon />}
            >
              New Event Type
            </Button>
            <Menu
              id="new-event-menu"
              anchorEl={anchorElNewEvent}
              open={openNewEventMenu}
              onClose={handleCloseNewEvent}
              MenuListProps={{
                "aria-labelledby": "new-event-button",
              }}
            >
              <MenuItem disabled={true}>
                <ListItemText variant="h5">Create Event On</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => handleCreateEventType(getEventTypeAddUrl())}
              >
                <ListItemText>{user?.name || "Personal"}</ListItemText>
              </MenuItem>

              <MenuItem disabled={true}>
                <ListItemText variant="h5">Teams</ListItemText>
              </MenuItem>

              {teams.map((team) => (
                <MenuItem
                  key={team.id}
                  onClick={() =>
                    handleCreateEventType(getTeamEventTypeUrl(team.id))
                  }
                >
                  <ListItemText>{team.name}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </div>
        </Grid>
      </Grid>

      <Divider my={6} />
      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      <SearchBar
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder="Search event types..."
        containerSx={{
          borderBottom: "1px solid rgba(224, 224, 224, 0.4)",
        }}
      />

      <Paper>
        {/* Filter Button */}
        <Box p={2} borderBottom="1px solid rgba(224, 224, 224, 0.4)">
          <Button
            startIcon={<FilterListIcon />}
            onClick={handleClickFilter}
            variant="outlined"
            size="small"
          >
            {filterType === "all" && "All Events"}
            {filterType === "personal" && "My Events"}
            {filterType === "allTeams" && "All Team Events"}
            {filterType === "team" && "Team Events"}
          </Button>
          <Menu
            anchorEl={anchorElFilter}
            open={openFilterMenu}
            onClose={handleCloseFilter}
          >
            <MenuItem
              onClick={() => {
                setFilterType("all");
                handleCloseFilter();
              }}
            >
              All Events
            </MenuItem>
            <MenuItem
              onClick={() => {
                setFilterType("personal");
                handleCloseFilter();
              }}
            >
              My Events
            </MenuItem>
            <MenuItem
              onClick={() => {
                setFilterType("allTeams");
                handleCloseFilter();
              }}
            >
              All Team Events
            </MenuItem>
            <MenuItem>
              <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="team-filter-label">Select Team</InputLabel>
                <Select
                  labelId="team-filter-label"
                  value={selectedTeamId}
                  label="Select Team"
                  onChange={(e) => {
                    setSelectedTeamId(e.target.value);
                    setFilterType("team");
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {teams.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MenuItem>
          </Menu>
        </Box>

        {/* ReusableTable replaces both renderTableView and renderCardView */}
        <ReusableTable
          data={processedEventTypes}
          columns={columns}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search event types..."
          searchFields={searchFields}
          selected={selected}
          onSelectAllClick={handleSelectAllClick}
          onSelect={handleClick}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          viewStyle={viewStyle}
          setViewStyle={setViewStyle}
          hasCardView={true}
          CardComponent={EventTypeCard}
          cardProps={{ xs: 12, sm: 6, md: 4, lg: 3 }}
          onBulkEdit={handleBulkEdit}
          onBulkDelete={handleBulkDelete}
          customActions={[]}
          title=""
          showSearch={false}
        />
      </Paper>

      {/* Bulk Delete Dialog */}
      <Dialog
        open={bulkDeleteDialog}
        onClose={() => setBulkDeleteDialog(false)}
      >
        <DialogTitle>Confirm Bulk Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selected.length} selected event
            types? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleBulkDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete {selected.length} Event Types
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

export default EventTypes;
